/**
 * Report Issue Modal
 *
 * Allows users to report issues for a container.
 * Issue types are loaded dynamically from the backend.
 * Users can also create a custom issue type inline.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '@/lib/api-client';
import { toast } from '@/lib/toast-service';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';
import { Select } from '@/components/base/select/select';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import { XCircle, Plus } from '@untitledui/icons';
import { CONTAINER_ISSUES_QUERY_KEYS } from '@/hooks/api/use-container-issues';
import { useIssueTypes, ISSUE_TYPES_QUERY_KEYS } from '@/hooks/api/use-issue-types';

interface ReportIssueModalProps {
    containerId: string;
    containerNumber: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const SEVERITY_OPTIONS = [
    { id: 'low', label: 'Low', supportingText: 'Minor issue, no immediate action needed' },
    { id: 'medium', label: 'Medium', supportingText: 'Should be addressed soon' },
    { id: 'high', label: 'High', supportingText: 'Requires prompt attention' },
    { id: 'critical', label: 'Critical', supportingText: 'Immediate action required' },
];

const CATEGORY_OPTIONS = [
    { id: 'carrier_transport', label: 'Carrier / Transport' },
    { id: 'customs_regulatory', label: 'Customs / Regulatory' },
    { id: 'documentation', label: 'Documentation' },
    { id: 'cargo_condition', label: 'Cargo Condition' },
    { id: 'temperature_control', label: 'Temperature Control' },
    { id: 'external_event', label: 'External Event' },
    { id: 'financial_risk', label: 'Financial Risk' },
    { id: 'other', label: 'Other' },
];

export function ReportIssueModal({
    containerId,
    containerNumber: _containerNumber,
    isOpen,
    onClose,
    onSuccess,
}: ReportIssueModalProps) {
    const { t } = useTranslation('pages:containers');
    const queryClient = useQueryClient();
    const { data: issueTypes } = useIssueTypes();

    const [issueType, setIssueType] = useState<string | null>(null);
    const [severity, setSeverity] = useState<string>('medium');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [blocksShipment, setBlocksShipment] = useState(false);

    // Custom type fields
    const [isCustomType, setIsCustomType] = useState(false);
    const [customTypeLabel, setCustomTypeLabel] = useState('');
    const [customTypeCategory, setCustomTypeCategory] = useState<string | null>('other');

    const issueTypeItems = (issueTypes ?? []).map((t) => ({
        id: t.slug,
        label: t.label,
        supportingText: t.category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
    }));

    const resetForm = () => {
        setIssueType(null);
        setSeverity('medium');
        setTitle('');
        setDescription('');
        setBlocksShipment(false);
        setIsCustomType(false);
        setCustomTypeLabel('');
        setCustomTypeCategory('other');
    };

    const createIssueTypeMutation = useMutation({
        mutationFn: async (data: { slug: string; label: string; category: string }) => {
            return apiPost<{ slug: string }>('/issue-types', {
                slug: data.slug,
                label: data.label,
                category: data.category,
                risk_component: 'document', // default, can be adjusted later
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ISSUE_TYPES_QUERY_KEYS.all });
        },
    });

    const reportIssueMutation = useMutation({
        mutationFn: async (data: {
            container_id: string;
            issue_type: string;
            severity: string;
            title: string;
            description: string;
            blocks_shipment: boolean;
        }) => {
            return apiPost(`/container-issues/containers/${containerId}/issues`, data);
        },
        onSuccess: () => {
            toast.success(t('detail.actions.issueReported', 'Issue reported successfully'));
            queryClient.invalidateQueries({ queryKey: CONTAINER_ISSUES_QUERY_KEYS.list(containerId) });
            queryClient.invalidateQueries({ queryKey: CONTAINER_ISSUES_QUERY_KEYS.all });
            resetForm();
            onSuccess();
        },
        onError: (error) => {
            console.error('Failed to report issue:', error);
            toast.error(t('detail.actions.issueError', 'Failed to report issue'));
        },
    });

    const handleSubmit = async () => {
        let selectedType = issueType;

        if (isCustomType) {
            if (!customTypeLabel.trim()) {
                toast.error('Please enter a name for the custom issue type');
                return;
            }
            // Generate slug from label
            const slug = customTypeLabel
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_|_$/g, '');

            try {
                await createIssueTypeMutation.mutateAsync({
                    slug,
                    label: customTypeLabel.trim(),
                    category: customTypeCategory ?? 'other',
                });
                selectedType = slug;
            } catch (err: unknown) {
                // 409 = already exists, that's fine — use the slug
                const error = err as { status?: number };
                if (error?.status === 409) {
                    selectedType = slug;
                } else {
                    toast.error('Failed to create custom issue type');
                    return;
                }
            }
        }

        if (!selectedType) {
            toast.error(t('detail.actions.selectIssueType', 'Please select an issue type'));
            return;
        }
        if (!title.trim()) {
            toast.error(t('detail.actions.enterTitle', 'Please enter a title'));
            return;
        }

        reportIssueMutation.mutate({
            container_id: containerId,
            issue_type: selectedType,
            severity,
            title,
            description,
            blocks_shipment: blocksShipment,
        });
    };

    if (!isOpen) return null;

    const isPending = reportIssueMutation.isPending || createIssueTypeMutation.isPending;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal>
                <Dialog aria-label="Report Issue">
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('detail.actions.reportIssue', 'Report Issue')}
                                </h2>
                                <p className="text-sm text-tertiary mt-1">
                                    {t('detail.actions.reportIssueDesc', 'Report a problem with this container')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                className="text-tertiary hover:text-primary transition-colors"
                            >
                                <XCircle className="size-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="space-y-4">
                            {/* Issue Type — select or custom */}
                            {!isCustomType ? (
                                <div className="space-y-2">
                                    <Select
                                        label={`${t('detail.actions.issueType', 'Issue Type')} *`}
                                        placeholder={t('detail.actions.selectIssueType', 'Select issue type')}
                                        selectedKey={issueType}
                                        onSelectionChange={(key) => {
                                            if (key && typeof key === 'string') {
                                                setIssueType(key);
                                            }
                                        }}
                                        aria-label="Issue Type"
                                        size="md"
                                    >
                                        {issueTypeItems.map((item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        ))}
                                    </Select>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsCustomType(true);
                                            setIssueType(null);
                                        }}
                                        className="flex items-center gap-1.5 text-xs font-medium text-brand-secondary hover:text-brand-secondary_hover transition-colors"
                                    >
                                        <Plus className="size-3.5" />
                                        Add custom type
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3 rounded-lg border border-secondary p-3">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-primary">New Issue Type</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsCustomType(false);
                                                setCustomTypeLabel('');
                                                setCustomTypeCategory('other');
                                            }}
                                            className="text-xs font-medium text-tertiary hover:text-primary transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    <Input
                                        label="Type Name *"
                                        value={customTypeLabel}
                                        onChange={setCustomTypeLabel}
                                        placeholder="e.g. Phytosanitary Rejection"
                                        size="sm"
                                    />
                                    <Select
                                        label="Category"
                                        selectedKey={customTypeCategory}
                                        onSelectionChange={(key) => {
                                            if (key && typeof key === 'string') {
                                                setCustomTypeCategory(key);
                                            }
                                        }}
                                        aria-label="Issue Category"
                                        size="sm"
                                    >
                                        {CATEGORY_OPTIONS.map((opt) => (
                                            <Select.Item key={opt.id} id={opt.id}>
                                                {opt.label}
                                            </Select.Item>
                                        ))}
                                    </Select>
                                </div>
                            )}

                            <Select
                                label={`${t('detail.actions.severity', 'Severity')} *`}
                                placeholder={t('detail.actions.selectSeverity', 'Select severity')}
                                selectedKey={severity}
                                onSelectionChange={(key) => {
                                    if (key && typeof key === 'string') {
                                        setSeverity(key);
                                    }
                                }}
                                aria-label="Severity"
                                size="md"
                            >
                                {SEVERITY_OPTIONS.map((opt) => (
                                    <Select.Item key={opt.id} id={opt.id}>
                                        {opt.label}
                                    </Select.Item>
                                ))}
                            </Select>

                            <Input
                                label={`${t('detail.actions.issueTitle', 'Title')} *`}
                                value={title}
                                onChange={setTitle}
                                placeholder={t('detail.actions.issueTitlePlaceholder', 'Brief description of the issue')}
                                size="md"
                            />

                            <TextArea
                                label={t('detail.actions.issueDescription', 'Description')}
                                value={description}
                                onChange={setDescription}
                                placeholder={t('detail.actions.issueDescPlaceholder', 'Provide more details about the issue...')}
                                rows={4}
                            />

                            <Checkbox
                                isSelected={blocksShipment}
                                onChange={setBlocksShipment}
                                label={t('detail.actions.blocksShipment', 'This issue blocks the shipment')}
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 mt-6">
                            <Button color="secondary" onPress={onClose}>
                                {t('common:cancel', 'Cancel')}
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleSubmit}
                                isLoading={isPending}
                            >
                                {t('detail.actions.submitIssue', 'Submit Issue')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
